import { VueI18n } from "vue-i18n";
import { ApiErrorResponse } from "@/stores/errors/ApiErrorMessageBag.ts";

/**
 * Represents generic API errors that are not associated with a specific form field.
 *
 * This class handles non-field-specific errors such as:
 * - Authentication/authorization errors
 * - Network connectivity issues
 * - Server-side errors (500 Internal Server Error)
 * - Business logic errors that don't relate to a particular input field
 *
 * Unlike `FieldValidationError`, these errors are typically displayed at the form level
 * or in a global error notification rather than next to a specific input field.
 *
 * @example
 * ```typescript
 * const networkError = new GenericApiError([
 *   { key: 'NETWORK_ERROR', message: 'Connection failed', parameters: {} }
 * ]);
 *
 * // Display in a toast notification
 * toast.error(networkError.toLocaleString(i18n));
 *
 * // Log for debugging
 * console.error(networkError.toString());
 * ```
 */
export class GenericApiError {

	/**
	 * Creates a new GenericApiError instance.
	 *
	 * @param errorObjects - An array of error response objects containing the error details.
	 *                       Each object includes:
	 *                       - `key`: A unique error identifier for i18n lookup
	 *                       - `message`: A fallback message when translation is unavailable
	 *                       - `parameters`: Dynamic values for message interpolation
	 */
	constructor(
		private errorObjects: ApiErrorResponse[]
	) {}

	/**
	 * Converts all error messages to a non-localized, comma-separated string.
	 *
	 * This method returns the raw error messages from the API without any translation,
	 * making it useful for logging, debugging, or when i18n is not available.
	 *
	 * @returns A comma-separated string of all raw error messages.
	 *
	 * @example
	 * ```typescript
	 * console.error('API Error:', genericError.toString());
	 * // Output: "Network connection failed, Request timeout"
	 * ```
	 */
	toString() {
		return this.errorObjects.map(
			(errorObject) => {
				return errorObject.message;
			}
		).join(', ');
	}

	/**
	 * Converts all error messages to a localized, comma-separated string.
	 *
	 * This method translates each error using the provided i18n instance and
	 * interpolates any dynamic parameters. Error keys are prefixed with '#error: '
	 * for i18n lookup (e.g., '#error: network error').
	 *
	 * @param i18n - Optional Vue I18n instance for translation lookup.
	 *               If not provided, returns the raw error messages.
	 * @returns A comma-separated string of all localized error messages.
	 *
	 * @example
	 * ```typescript
	 * const message = genericError.toLocaleString(i18n);
	 * // With Dutch locale: "Netwerkverbinding mislukt"
	 * // Without i18n: "Network connection failed"
	 * ```
	 */
	toLocaleString(i18n?: VueI18n) {
		return this.errorObjects.map(
			(errorObject) => {
				const errorMessageI18nKey = this.getErrorMessage(errorObject);
				if (!i18n) {
					return errorObject.message;
				}

				if (errorObject.parameters) {
					return i18n.t('#error: ' + errorMessageI18nKey);
				} else {
					return i18n.t('#error: ' + errorMessageI18nKey, errorObject.parameters);
				}
			}
		).join(', ');
	};

	/**
	 * Maps API error keys to user-friendly i18n translation keys.
	 *
	 * This method provides a mapping layer between backend error codes and
	 * frontend i18n keys. It handles various error scenarios including:
	 * - Permission errors (e.g., 'CHANGE_OWN_STATUS', 'DEMOTE_LAST_OWNER')
	 * - Resource errors (e.g., ':ENTITY_NOT_FOUND', 'DELETE_FAILED')
	 * - Validation errors (e.g., 'DATE_RANGE_REQUIRED', 'FILTER_REQUIRED')
	 * - Business logic errors (e.g., 'CLASSROOM_PAUSED', 'LESSON_ITEM_FULL')
	 *
	 * Error keys starting with ':' indicate a parameterized error where the
	 * parameter name (e.g., 'ENTITY') should be replaced with an actual value.
	 *
	 * @param errorObject - The error response object containing the API error key.
	 * @returns The mapped i18n key for translation, or the original message
	 *          if no mapping exists.
	 *
	 * @example
	 * ```typescript
	 * // API error key: 'COLLECTION_NOT_EMPTY'
	 * // Returns: 'collection not empty'
	 * // i18n lookup: '#error: collection not empty'
	 *
	 * // API error key: ':ENTITY_NOT_FOUND' with parameters: { entity: 'User' }
	 * // Returns: '{entity} not found'
	 * // i18n lookup: '#error: {entity} not found' → "User not found"
	 * ```
	 */
	getErrorMessage(errorObject: ApiErrorResponse){

		const validationErrors = [
			{ key: 'CHANGE_OWN_STATUS', errorMessage: "change own status" },
			{ key: 'CLASSROOM_PAUSED', errorMessage: "classroom paused" },
			{ key: 'COLLECTION_NOT_EMPTY', errorMessage: "collection not empty" },
			{ key: 'DATE_RANGE_MUST_BE_LESS_THAN_:DAYS_DAYS', errorMessage: "date range must be less than amount days" },
			{ key: 'DATE_RANGE_REQUIRED', errorMessage: "date range required" },
			{ key: 'DELETE_FAILED', errorMessage: "delete failed" },
			{ key: 'DEMOTE_LAST_OWNER', errorMessage: "demote last owner" },
			{ key: 'DESTINATION_NOT_EMPTY', errorMessage: "destinations have existing lessons" },
			{ key: ':ENTITY_NOT_FOUND', errorMessage: "{entity} not found" },
			{ key: ':ENTITY_REQUIRED', errorMessage: "{entity} required" },
			{ key: 'FILE_SAVE_FAILED', errorMessage: "file save failed" },
			{ key: 'FILTER_REQUIRED', errorMessage: "filter required" },
			{ key: 'ICON_STILL_IN_USE', errorMessage: "icon is still in use" },
			{ key: 'LESSON_COPY_INVALID_DURATION', errorMessage: "lesson copy invalid duration" },
			{ key: 'LESSON_ITEM_FULL', errorMessage: "lesson item full" },
			{ key: 'PUPILS_WILL_BE_CHECKED_OUT', errorMessage: "pupils will be checked out" },
			{ key: 'REQUIRES_EVENT_IDS', errorMessage: "requires event ids" },
			{ key: 'SAVE_FAILED', errorMessage: "save failed" },
			{ key: 'OUTDATED_LESSON_PLANS', errorMessage: "source has outdated lesson plans" },
			{ key: 'TEACHERS_NOT_PART_OF_ORGANISATION', errorMessage: "teachers not part of organisation" },
			{ key: 'USER_IS_NOT_A_TEACHER', errorMessage: "user is not a teacher" },
		];

		const validationError = validationErrors.find(item => item.key === errorObject.key);
		return validationError ? validationError.errorMessage : errorObject.message;
	}
}
